import { AppImages, variantProvider } from '@/app'
import { View, Image } from '@/components'
import { CSSProperties, useMemo } from 'react'
import { FeedItemActions } from './molecules/FeedItemActions'
import { ContributeButton } from './atoms/ContributeButton'
import { feedItemsByType } from './FeedItems'
import { RenderItemProps } from './FeedItems/types'
import { ReportOverlay } from './organisms/ReportOverlay'
import { useIsFocusedFeedItem } from './feedScroll'
import { motion } from 'framer-motion'
import { FeedHelpOverlay } from './atoms/HelpOverlay'
import { FeedDimensions } from './FeedItems/shared'
import { memoBy } from '@codeleap/utils'
import { TypeGuards } from '@codeleap/types'

const BREAKPOINT = 'small'
import { FeedTextOverlay } from './molecules/TextOverlay'

const SideControls = memoBy(({ item, index, style }: RenderItemProps & { style?: CSSProperties }) => {
  const isFocused = useIsFocusedFeedItem(index)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isFocused ? 1 : 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      css={[{ pointerEvents: isFocused ? 'auto' : 'none' }, styles.side, style]}
    >
      <View />

      <FeedItemActions item={item}>
        <ContributeButton />
      </FeedItemActions>
    </motion.div>
  )
}, ['item', 'index'])

export const FeedItem = (props: RenderItemProps) => {
  const { item, index } = props

  const Content = useMemo(() => {
    if ('profile_prompt' in item && !TypeGuards.isNull(item.profile_prompt)) {
      props.item.type = 'profile_prompt'

      return feedItemsByType.profile_prompt
    }

    return feedItemsByType[item.type] ?? feedItemsByType['text']
  }, [item])

  const backgroundImg = useMemo(() => {
    return {
      text: AppImages.feedBgText,
      hurdle: AppImages.hurdleBg,
      prompt: AppImages.feedBg,
      review: AppImages.feedBg,
      review_question: AppImages.feedBg,
    }?.[item?.type]
  }, [item?.type])

  if (!Content) return null

  const contentOverlay = <FeedTextOverlay {...FeedTextOverlay.extractProps(item)} />

  return (
    <View style={styles.feedWrapper}>
      <View style={styles.feedItem}>
        {backgroundImg ? (
          <Image
            source={backgroundImg}
            style={styles.backgroundImg}
          />
        ) : null}

        <Content {...props} contentOverlay={contentOverlay}/>

        <SideControls {...props} style={styles.internalActions} />
        <ReportOverlay id={item?.id} />
        {index === 0 ? <FeedHelpOverlay /> : null}
      </View>

      <SideControls {...props} style={styles.externalActions} />
    </View>
  )
}

const styles = variantProvider.createComponentStyle(theme => ({
  feedWrapper: {
    ...theme.presets.row,
    ...theme.presets.justifyStart,
    ...theme.spacing.gap(2.5),
    paddingTop: FeedDimensions.spacingTop,
    paddingBottom: FeedDimensions.spacingBottom,

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      paddingTop: 0,
      paddingBottom: 0,
    }
  },
  feedItem: {
    minWidth: FeedDimensions.WIDTH,
    maxWidth: FeedDimensions.WIDTH,
    minHeight: FeedDimensions.HEIGHT,
    maxHeight: FeedDimensions.HEIGHT,

    backgroundColor: '#fff2',
    borderRadius: 24,
    overflow: 'hidden',
    zIndex: 0,

    ...theme.presets.center,
    ...theme.presets.column,
    ...theme.presets.relative,

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      maxWidth: FeedDimensions.WIDTH_WITHOUT_SPACING,
      minWidth: FeedDimensions.WIDTH_WITHOUT_SPACING,
      minHeight: FeedDimensions.HEIGHT_WITHOUT_SPACING,
      maxHeight: FeedDimensions.HEIGHT_WITHOUT_SPACING,
      borderRadius: 0,
    }
  },
  side: {
    ...theme.presets.column,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    display: 'flex',
    ...theme.spacing.paddingVertical(1.5),
    
    [theme.media.down(BREAKPOINT)]: {
      position: 'absolute',
      bottom: 0,
      right: theme.spacing.value(2),
      zIndex: 5,
      alignItems: 'flex-end'
    }
  },
  internalActions: {
    display: 'none',

    [theme.media.down(BREAKPOINT)]: {
      display: 'flex',
    }
  },
  externalActions: {
    display: 'flex',

    [theme.media.down(BREAKPOINT)]: {
      display: 'none',
    }
  },
  backgroundImg: {
    ...theme.presets.absolute,
    ...theme.presets.whole,
    width: FeedDimensions.WIDTH,
    height: FeedDimensions.HEIGHT,
    objectFit: 'cover',
    zIndex: 0,

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      width: FeedDimensions.WIDTH_WITHOUT_SPACING,
      height: FeedDimensions.HEIGHT_WITHOUT_SPACING,
    }
  },
}), true)
